/**
 * Alcance (frames 86:2, 304:11029, 632:19479 states 3-5): one row -- the influence level as its
 * key chip, the chosen places as removable value chips, then a `+` that opens a popover.
 *
 * The level is a closed five-value list (`InfluenceScope`) that is never created; its places are
 * searched against a platform list and never created either. Choosing a new level clears the
 * chosen places and offers undo, since a place from the old level would no longer resolve at the
 * new one.
 *
 * `usePlaceSearch` below searches `api`'s `GET /tenants/{t}/places` (`./api/schemas.ts`'s
 * `placeSchema`, mocked by `./api/handlers.ts`). `uf` is `null` at country level, so
 * `placeLabel` below renders the name alone rather than the usual `name · uf` chip.
 *
 * At Bairro level the place step cascades: a municipality is chosen first and its bairros are
 * listed under it, since a bare bairro name ("Centro") repeats across the country. The same
 * search also finds a bairro typed directly, as "Jaraguá" or "Jaraguá, Maceió". Stopping at the
 * municipality registers the account at Município level instead.
 */
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, CircleAlert, Plus } from 'lucide-react';

import { IconHint } from '../../../chrome/hint';
import { defineCatalogue } from '../../../i18n';
import { Button } from '../../../ui/button';
import { Chip } from '../../../ui/chip';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../ui/command';
import { FieldSet, FieldLegend } from '../../../ui/field';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { useApiClient } from '../../session/client';
import { type InfluenceScope, type Place } from './api/schemas';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';

const reachCatalogue = defineCatalogue(
  {
    label: 'Alcance',
    info: 'Escolha o nível e os lugares onde a conta é ouvida. Trocar o nível limpa os lugares.',
    addEmpty: 'nível e lugares',
    addPlace: 'lugar',
    changeLevel: 'Trocar o nível de alcance, hoje {level}',
    levelSearchPlaceholder: 'Nível',
    placeChip: '{name} · {uf}',
    placeSearchPlaceholder: 'Buscar lugar em {level}',
    placeEmpty: 'Nenhum lugar encontrado.',
    placeSearching: 'Buscando lugares…',
    placeError: 'Não foi possível buscar lugares.',
    bairroChip: '{name} — {parent}/{uf}',
    municipalitySearchPlaceholder: 'Município, ou Bairro, Município',
    bairroSearchPlaceholder: 'Buscar bairro em {parent}',
    municipalityGroup: 'Municípios',
    bairroGroup: 'Bairros',
    wholeMunicipality: 'Todo o município de {name}',
    removePlace: 'Remover {place}',
    undoLevelChange: 'Lugares removidos ao trocar o nível.',
    undo: 'Desfazer',
    'level.neighborhood': 'Bairro',
    'level.district': 'Distrito',
    'level.municipality': 'Município',
    'level.state': 'Estado',
    'level.country': 'País',
  },
  {
    label: 'Reach',
    info: 'Choose the level and the places where the account is heard. Changing the level clears the places.',
    addEmpty: 'level and places',
    addPlace: 'place',
    changeLevel: 'Change the reach level, today {level}',
    levelSearchPlaceholder: 'Level',
    placeChip: '{name} · {uf}',
    placeSearchPlaceholder: 'Search places in {level}',
    placeEmpty: 'No place found.',
    placeSearching: 'Searching places…',
    placeError: 'Places could not be searched.',
    bairroChip: '{name} — {parent}/{uf}',
    municipalitySearchPlaceholder: 'Municipality, or Neighbourhood, Municipality',
    bairroSearchPlaceholder: 'Search neighbourhoods in {parent}',
    municipalityGroup: 'Municipalities',
    bairroGroup: 'Neighbourhoods',
    wholeMunicipality: 'All of {name}',
    removePlace: 'Remove {place}',
    undoLevelChange: 'Places removed when the level changed.',
    undo: 'Undo',
    'level.neighborhood': 'Neighbourhood',
    'level.district': 'District',
    'level.municipality': 'Municipality',
    'level.state': 'State',
    'level.country': 'Country',
  },
);
const rft = reachCatalogue.t;

const LEVELS: { value: InfluenceScope; label: `level.${InfluenceScope}` }[] = [
  { value: 'neighborhood', label: 'level.neighborhood' },
  { value: 'district', label: 'level.district' },
  { value: 'municipality', label: 'level.municipality' },
  { value: 'state', label: 'level.state' },
  { value: 'country', label: 'level.country' },
];

function levelLabel(scope: InfluenceScope): string {
  return rft(LEVELS.find((level) => level.value === scope)!.label);
}

function placeLabel(place: Place, level: InfluenceScope | null): string {
  if (place.uf === null) return place.name;
  if (level === 'neighborhood' && place.parent_name) {
    return rft('bairroChip', { name: place.name, parent: place.parent_name, uf: place.uf });
  }
  return rft('placeChip', { name: place.name, uf: place.uf });
}

export interface ReachValue {
  scope: InfluenceScope | null;
  places: Place[];
}

export const EMPTY_REACH: ReachValue = { scope: null, places: [] };

function usePlaceSearch(
  tenantId: string,
  scope: InfluenceScope | null,
  query: string,
  parentId: string | null = null,
  enabled = true,
) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['tenant', tenantId, 'places', scope, query, parentId],
    queryFn: async () => {
      const params = new URLSearchParams({ level: scope ?? '', q: query });
      if (parentId !== null) params.set('parent_id', parentId);
      const { items } = await client.getParsed(
        routePath(GET_ROUTES.places, { tenantId }, params),
        GET_ROUTES.places.schema,
      );
      return items;
    },
    enabled: scope !== null && enabled,
  });
}

export function ReachField(props: {
  tenantId: string;
  value: ReachValue;
  onChange: (value: ReachValue) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'level' | 'place'>('level');
  const [query, setQuery] = useState('');
  const [undoValue, setUndoValue] = useState<ReachValue | null>(null);
  const [municipality, setMunicipality] = useState<Place | null>(null);

  const cascading = props.value.scope === 'neighborhood';
  const choosingMunicipality = cascading && municipality === null;
  // "Bairro, Município" names a bairro, so only a bare name is also searched as a municipality.
  const municipalityEnabled = choosingMunicipality && !query.includes(',');
  const placeEnabled = !choosingMunicipality || query !== '';
  const search = usePlaceSearch(
    props.tenantId,
    props.value.scope,
    query,
    cascading ? (municipality?.id ?? null) : null,
    placeEnabled,
  );
  const municipalities = usePlaceSearch(
    props.tenantId,
    'municipality',
    query,
    null,
    municipalityEnabled,
  );

  function resetToStep(nextStep: 'level' | 'place'): void {
    setStep(nextStep);
    setQuery('');
    setMunicipality(null);
  }

  function chooseMunicipality(place: Place): void {
    setMunicipality(place);
    setQuery('');
  }

  function chooseWholeMunicipality(place: Place): void {
    setUndoValue(null);
    props.onChange({ scope: 'municipality', places: [place] });
    setOpen(false);
  }

  /** For a button that is not itself the `PopoverTrigger` (the level chip): that trigger's own
   *  click already toggles `open` through `onOpenChange`, so setting it again here would race the
   *  Radix-driven update when the popover is already open. */
  function openAt(nextStep: 'level' | 'place'): void {
    resetToStep(nextStep);
    setOpen(true);
  }

  function chooseLevel(scope: InfluenceScope): void {
    if (scope === props.value.scope) {
      resetToStep('place');
      return;
    }
    setUndoValue(props.value.places.length > 0 ? props.value : null);
    props.onChange({ scope, places: [] });
    resetToStep('place');
  }

  function addPlace(place: Place): void {
    setUndoValue(null);
    if (!props.value.places.some((existing) => existing.id === place.id)) {
      props.onChange({ ...props.value, places: [...props.value.places, place] });
    }
    setOpen(false);
  }

  function removePlace(id: string): void {
    props.onChange({ ...props.value, places: props.value.places.filter((p) => p.id !== id) });
  }

  function undo(): void {
    if (undoValue === null) return;
    props.onChange(undoValue);
    setUndoValue(null);
  }

  function onPlaceSearchKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== 'Backspace' || query !== '') return;
    if (municipality !== null) setMunicipality(null);
    else openAt('level');
  }

  function placeSearchPlaceholder(): string {
    if (choosingMunicipality) return rft('municipalitySearchPlaceholder');
    if (cascading) return rft('bairroSearchPlaceholder', { parent: municipality!.name });
    return rft('placeSearchPlaceholder', {
      level: props.value.scope === null ? '' : levelLabel(props.value.scope),
    });
  }

  // A disabled query reports `isPending`, so each is read only while enabled.
  const municipalityRows = municipalityEnabled ? (municipalities.data ?? []) : [];
  const placeRows = placeEnabled ? (search.data ?? []) : [];
  const pending =
    (municipalityEnabled && municipalities.isPending) || (placeEnabled && search.isPending);
  const failed =
    (municipalityEnabled && municipalities.isError) || (placeEnabled && search.isError);

  return (
    <FieldSet>
      <FieldLegend variant="label" className="flex items-center gap-1.5">
        {rft('label')}
        <IconHint label={rft('info')}>
          <button type="button" className="text-muted-foreground">
            <CircleAlert aria-hidden className="size-3.5" />
          </button>
        </IconHint>
      </FieldLegend>

      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
      >
        <PopoverAnchor asChild>
          <div className="flex flex-wrap items-center gap-1.5">
            {props.value.scope !== null && (
              <button
                type="button"
                onClick={() => openAt('level')}
                aria-label={rft('changeLevel', { level: levelLabel(props.value.scope) })}
                className="rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <Chip tone="neutral">{levelLabel(props.value.scope)}</Chip>
              </button>
            )}
            {props.value.places.map((place) => (
              <Chip
                key={place.id}
                tone="outline"
                onClose={() => removePlace(place.id)}
                closeLabel={rft('removePlace', { place: place.name })}
              >
                {placeLabel(place, props.value.scope)}
              </Chip>
            ))}
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => resetToStep(props.value.scope === null ? 'level' : 'place')}
              >
                <Plus aria-hidden />
                {props.value.scope === null ? rft('addEmpty') : rft('addPlace')}
              </Button>
            </PopoverTrigger>
          </div>
        </PopoverAnchor>
        <PopoverContent className="w-64 p-0" align="start">
          <Command shouldFilter={step === 'level'}>
            {step === 'level' ? (
              <>
                <CommandInput placeholder={rft('levelSearchPlaceholder')} />
                <CommandList>
                  <CommandGroup>
                    {LEVELS.map((level) => (
                      <CommandItem
                        key={level.value}
                        value={rft(level.label)}
                        onSelect={() => chooseLevel(level.value)}
                      >
                        <Check
                          aria-hidden
                          className={props.value.scope === level.value ? '' : 'invisible'}
                        />
                        {rft(level.label)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </>
            ) : (
              <>
                <CommandInput
                  value={query}
                  onValueChange={setQuery}
                  onKeyDown={onPlaceSearchKeyDown}
                  placeholder={placeSearchPlaceholder()}
                />
                <CommandList>
                  {pending && (
                    <p className="p-4 text-sm text-muted-foreground">{rft('placeSearching')}</p>
                  )}
                  {failed && <p className="p-4 text-sm text-destructive">{rft('placeError')}</p>}
                  {!pending && !failed && (
                    <>
                      <CommandEmpty>{rft('placeEmpty')}</CommandEmpty>
                      {cascading && municipality !== null && props.value.places.length === 0 && (
                        <CommandGroup>
                          <CommandItem
                            value={`whole-${municipality.id}`}
                            onSelect={() => chooseWholeMunicipality(municipality)}
                          >
                            {rft('wholeMunicipality', { name: municipality.name })}
                            <span className="ml-auto text-muted-foreground">{municipality.uf}</span>
                          </CommandItem>
                        </CommandGroup>
                      )}
                      {municipalityRows.length > 0 && (
                        <CommandGroup heading={rft('municipalityGroup')}>
                          {municipalityRows.map((place) => (
                            <CommandItem
                              key={place.id}
                              value={place.id}
                              onSelect={() => chooseMunicipality(place)}
                            >
                              {place.name}
                              <span className="ml-auto text-muted-foreground">{place.uf}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      {placeRows.length > 0 && (
                        <CommandGroup
                          heading={choosingMunicipality ? rft('bairroGroup') : undefined}
                        >
                          {placeRows.map((place) => (
                            <CommandItem
                              key={place.id}
                              value={place.id}
                              onSelect={() => addPlace(place)}
                            >
                              {choosingMunicipality
                                ? placeLabel(place, props.value.scope)
                                : place.name}
                              {place.uf !== null && !choosingMunicipality && (
                                <span className="ml-auto text-muted-foreground">{place.uf}</span>
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </>
                  )}
                </CommandList>
              </>
            )}
          </Command>
        </PopoverContent>
      </Popover>

      {undoValue !== null && (
        <p className="text-sm text-muted-foreground">
          {rft('undoLevelChange')}{' '}
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={undo}>
            {rft('undo')}
          </Button>
        </p>
      )}
    </FieldSet>
  );
}
